import {FatOutlineButton} from "../../common/component/FatOutlineButton";
import 'filepond/dist/filepond.min.css';

import React, {useLayoutEffect, useMemo, useState} from "react";
import {FilePondFile} from "filepond";
import {FormProvider, useForm} from "react-hook-form";
import {
    AuthorizationInfo,
    AuthorizationRecordType,
    AuthorizationRecordTypeMap,
    ClientReportReportTypeMap,
    ClientReportSubmitInput,
    clientReportTypeByAuthRecordType,
    isAddendumRequestReasonRequired, isClientReportStatusInAuthorized, isClientReportStatusInReview, isDEReport, isDiagEvalReport,
    isShortAddendumReport,
    isTransferReevaluationAuth,
    PendingAuthorizationInfo,
    ReportServiceType,
    requireServiceType,
    serviceTypeOptionsAvailableForAllTiers,
    shouldShowLastDayOfService,
    shouldShowNewAuthStartDate,
    TransferReevaluationAuthClientReportType,
    TransferReevaluationAuthClientReportTypeFromValueMap,
    YesNoOptionMap,
} from "./domain";
import {addBusinessDays, parse} from "date-fns";
import {ModalDatePicker} from "./form-components/ModalDatePicker";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {LoadingSpinner, SelectOption} from "../../common";
import {AuthorizationInput} from "./client-report/AuthorizationInput";
import {CommentsInput} from "./client-report/CommentsInput";
import {ClientReportTypeInput} from "./client-report/ClientReportTypeInput";
import {ProviderPersonLookupMultipleInput} from "../notification/ProviderPersonLookupMultipleInput";
import {
    ClientReportDialogManager,
    ClientReportDialogSubmitParams,
    shouldShowClientReportDialog
} from "./client-report/ClientReportDialogManager";
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {ClinicalRecommendationInput} from "./form-components/auth-hours/ClinicalRecommendationInput";
import {ClientOfferInput} from "./form-components/auth-hours/ClientOfferInput";
import {buildClientReportPayload, submitReport, useOktaAccessToken} from "./domain/client-report";
import {useLatestOfferedHoursForClientReport} from "./client-report/useLatestOfferedHoursForClientReport";
import {useObservable} from "../../rxsupport";
import {ReportModal} from "./form-components/ReportModal";
import {ShortAddendumInput} from "./quick-addendum/ShortAddendumInput";
import {StaffingAddendumReport} from "./staffing-plan/domain";
import {useStaffingAddendumReports} from "./domain/staffing-addendum";
import {isEmpty} from "lodash";
import {AddendumRequestReasonInput} from "./client-report/AddendumRequestReasonInput";
import {ServiceRecommended, ServiceRecommendedOptions} from "./form-components/auth-hours/domain";
import {ScannedFileInput} from "./ScannedFileInput";
import {VinelandIncludedInput} from "./client-report/VinelandIncludedInput";
import { YesNoOption } from "./domain";
import { showToast } from "common";
import {useClientReportModalCleanup, useClientReportModalPrefill} from "./client-report/useClientReportModalEffects";

type ClientReportModalProps = {
    showModal: boolean;
    clientGuid: string;
    defaultAuth?: AuthorizationInfo;
    recordTypeOption?: Map<string, string>;
    authorizationList?: AuthorizationInfo[];
    refetch?: () => void;
    hideModalFunc: () => void;
    staffingAddendumReport?: StaffingAddendumReport;
    authBillingTab? : boolean;
}

type SubmitWithFormDataParams = {
    data: ClientReportSubmitInput;
    isReportFileValid?: boolean;
    shouldForceCreateNewOperationCase?: boolean;
    reviseReportType?: string;
    pendingAuthToUpdate?: PendingAuthorizationInfo;
    shouldUpdateActiveAuth?: boolean;
};



// TODO we should move FilePond logic to outside this component
export function ClientReportModal({
                                      showModal,
                                      clientGuid,
                                      recordTypeOption,
                                      defaultAuth,
                                      authorizationList,
                                      hideModalFunc,
                                      refetch,
                                      staffingAddendumReport = undefined,
                                      authBillingTab = false
                                  }: ClientReportModalProps) {
    const staffingAddendumReportType = useMemo(() => staffingAddendumReport?.type ?? '', [staffingAddendumReport]);
    const modalTitle = useMemo(() => {
        switch (staffingAddendumReportType) {
            case 'ABA_ADDENDUM_REPORT':
                return 'ABA Addendum Report';
            case 'ABA_SHORT_ADDENDUM':
                return 'Change Hours Addendum';
            case '':
            default:
                return 'Client Report';
        }
    }, [staffingAddendumReportType]);
    const defaultReportType: string = useMemo(() => {
        return isEmpty(staffingAddendumReportType)
            ? (isTransferReevaluationAuth(defaultAuth) ? TransferReevaluationAuthClientReportTypeFromValueMap.get(TransferReevaluationAuthClientReportType.ABA_TRANSFER_REEVALUATION_REPORT)! : '')
            : staffingAddendumReportType;
    }, [staffingAddendumReportType, defaultAuth]);
    const methods = useForm<ClientReportSubmitInput>({
        defaultValues: {
            clientGuid: clientGuid,
            serviceType: [],
            addendumRequestReason: [],
        }
    });
    const {isStaffingAddendum, isShortAddendum, setIsShortAddendum} = useStaffingAddendumReports({
        methods,
        staffingAddendumReportType
    });

    // BHPNPP-3083 addendum reports must confirm intent to submit before showing the form, same as Staffing Plan
    
    const skipSubmitPrompt = !authBillingTab || !isStaffingAddendum;

    const [userWantsToSubmit, setUserWantsToSubmit] = useState(skipSubmitPrompt);

    useLayoutEffect(() => {
        if (showModal) {
            setUserWantsToSubmit(skipSubmitPrompt);
        }
    }, [showModal, skipSubmitPrompt]);

    const [uploadedFiles, setUploadedFiles] = useState<FilePondFile[]>([]);
    const isTherapyAuth = AuthorizationRecordTypeMap.get(defaultAuth?.recordType ?? "") === AuthorizationRecordType.Therapy_Treatment_Authorization;
    const isDEAuth = AuthorizationRecordTypeMap.get(defaultAuth?.recordType ?? "") === AuthorizationRecordType.Diagnostic_Evaluation_Authorization;

    const [isScannedFileValid$] = useState(new BehaviorSubject<boolean>(false));
    const [shouldValidate$] = useState(new BehaviorSubject<boolean>(false));
    const [notifyUsers, setNotifyUsers] = useState<SelectOption[]>([]);
    const [selectedAuth, setSelectedAuth] = useState<AuthorizationInfo | undefined>();
    const showConfirmationDialog$ = useMemo(() => new BehaviorSubject<boolean>(false), []);
    
    const activeAuth = selectedAuth || defaultAuth;
    // CCM-2707
    const restrictedOptions = useMemo(
        () => activeAuth ? serviceTypeOptionsAvailableForAllTiers(activeAuth)?.options ?? null : null,
        [activeAuth],
    );
    const watchedReportType = methods.watch('reportType');
    const watchedServiceTypes = methods.watch('serviceType') as ReportServiceType[];
    const watchedServiceTypesKey = JSON.stringify(watchedServiceTypes);
    const watchedServiceTypesForPrefill = useMemo(
        () => watchedServiceTypes ?? [],
        [watchedServiceTypesKey], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const {isRunning$, transitionRequestData} = useLatestOfferedHoursForClientReport(showModal, methods, activeAuth?.guid, watchedServiceTypesForPrefill);
    const isFetchingLatestHoursData = useObservable(isRunning$, false);

    const {clearFormData} = useClientReportModalCleanup({
        showModal,
        methods,
        setSelectedAuth,
        setUploadedFiles,
        setNotifyUsers,
        setIsShortAddendum,
    });

    useClientReportModalPrefill({
        showModal,
        activeAuth,
        userWantsToSubmit,
        reportType: watchedReportType,
        serviceTypes: watchedServiceTypesForPrefill,
        methods,
        isFetchingLatestHoursData,
        restrictedOptions,
        defaultReportType,
    });

    const accessToken = useOktaAccessToken();

    // BHPNPP-2596
    const mustIncludeVineland = isDEReport(methods.getValues('reportType'))
        && methods.getValues('vinelandIncluded') === YesNoOptionMap.get(YesNoOption.YES);

    const onReportTypeChanged = () => {
        setIsShortAddendum(isShortAddendumReport(methods.getValues('reportType')));

        // clear date inputs
        methods.setValue("firstTreatmentOfferedDate", "");
        methods.setValue("lastDayOfServiceBilled", "");
        methods.setValue("newAuthorizationStartDate", "");
        methods.setValue("finalDiagnosticContactDate", "");
    }

    const clearRecordTypeSelect = () => {
        methods.setValue("reportType", "");
    }

    const {promiseInProgress} = usePromiseTracker();

    const cancel = () => {
        if (promiseInProgress) {
            return;
        }

        if (staffingAddendumReport && authBillingTab && !userWantsToSubmit) {
            const in2BizDay = addBusinessDays(new Date(), 2).toDateString();
            showToast.warning(`Submission for ${modalTitle} is cancelled. Addendum needs to be submitted by ${in2BizDay}`,
                {duration: 15}
            );
        } else {
            showToast.warning(`Submission for ${modalTitle} is cancelled`);
        }

        clearFormData();

        hideModalFunc();
    }

    const resetAndRefetch = () => {
        clearFormData();
        if (refetch) {
            refetch();
        }
    }

    const isSubmittedFilesCorrect = async () => {
        if (isShortAddendum) {
            // no file upload is needed for short addendum
            return true;
        }

        shouldValidate$.next(true);
        return await firstValueFrom(isScannedFileValid$);
    }

    const submitWithFormData = ({data, isReportFileValid = false, shouldForceCreateNewOperationCase = false, reviseReportType, pendingAuthToUpdate, shouldUpdateActiveAuth}: SubmitWithFormDataParams) => {
         if (!isReportFileValid) {
            return;
        }
        if (requireServiceType(data.reportType) && !data.serviceType) {
            showToast.error('Require Service Type');
            return;
        }

        const formData = buildClientReportPayload(data, shouldForceCreateNewOperationCase, shouldUpdateActiveAuth);
        formData.append('clientGuid', data.clientGuid);

        if (defaultAuth) {
            formData.append('authorizationGuid', defaultAuth.guid);
        }
        // notify revise report type
        if (reviseReportType) {
            formData.append('reviseReportType', reviseReportType);
        }
        if (pendingAuthToUpdate) {
            formData.append('sfPendingAuthToUpdate', pendingAuthToUpdate.sfId);
        }

        if (transitionRequestData?.sfId && data.recommendationDifferenceRationale) {
            formData.append('missmatchTransferRequestCaseSfId', transitionRequestData.sfId);
            formData.append('missmatchTransferRequestCaseNumber', transitionRequestData.caseNumber);
        }

        notifyUsers.forEach((user) => {
            formData.append('notifyUserGuids', user.value);
        })

        if (!isShortAddendum) {
            uploadedFiles.forEach((file) => {
                formData.append('documents', file.file)
            });
        }

        void trackPromise(
            submitReport(formData, accessToken).then(async (response) => {
                if (!response.ok) {
                    const theMsg = `Failed to submit client report, system responded with code ${response.status}`;
                    response.json()
                        .then((body) => console.error("Failed to submit client report by error: ", body, JSON.stringify(Object.fromEntries(formData))))
                        .catch((err) => response.status !== 401 &&
                            console.error("Failed to submit client report by exception and unhandled error: ", err, JSON.stringify(Object.fromEntries(formData)))
                        );
                    showToast.error(theMsg);
                    return;
                }
                showToast.success(`${modalTitle} request successfully submitted.`);
                const {clientReportGuid = null} = await response.json();
                await staffingAddendumReport?.onFinished(clientReportGuid);
                hideModalFunc();
                resetAndRefetch();
            }).catch((err) => {
                console.error("Failed to upload client report by unhandled error", err, JSON.stringify(Object.fromEntries(formData)));
                const theMsg = `Unable to access report server. Error detail is ${err}`;
                showToast.error(theMsg);
            })
        );
    }
    const inReviewReportTypes = defaultAuth?.latestSubmittedClientReportByReportType
        ?.filter(item => isClientReportStatusInReview(item.reportStatus))
        .map(item => ({reportType: item.reportType, caseNumber: item.sfOperationCaseNumber})) ?? [];
    const authorizedReportTypes = defaultAuth?.latestSubmittedClientReportByReportType
        ?.filter(item => isClientReportStatusInAuthorized(item.reportStatus))
        .map(item => ({reportType: item.reportType, caseNumber: item.sfOperationCaseNumber})) ?? [];

    return (
        <ReportModal visible={showModal} title={`Submit ${modalTitle}`} onClose={cancel}>
            <FormProvider {...methods}>
                <form autoComplete="off"
                      onSubmit={async (e) => {
                          e.preventDefault();

                          if (isStaffingAddendum && !userWantsToSubmit) {
                              setUserWantsToSubmit(true);
                              return;
                          }

                          const isFileValid = await isSubmittedFilesCorrect();
                          const isValid = await methods.trigger();
                          
                          // Only proceed if form and file validations pass
                          if (!isValid || !isFileValid) {
                              return;
                          }
                          
                          const auth = selectedAuth || defaultAuth;
                          const reportType = methods.getValues('reportType');

                          if (shouldShowClientReportDialog(auth,  inReviewReportTypes, authorizedReportTypes, reportType)) {
                              showConfirmationDialog$.next(true);
                          } else {
                              submitWithFormData({
                                  data: methods.getValues(),
                                  isReportFileValid: isFileValid
                              });
                          }
                      }}>
                    {
                        !(isFetchingLatestHoursData || promiseInProgress) &&
                        <React.Fragment>
                            <div className="space-y-6">
                                <div className="flex flex-col w-full p-4 scrollbar-always"
                                     style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                    {isStaffingAddendum && !userWantsToSubmit && (
                                        <p>
                                            Do you want to submit {modalTitle} now?
                                        </p>
                                    )}
                                    {(!isStaffingAddendum || userWantsToSubmit) && (<>
                                    {
                                        authorizationList &&
                                        <AuthorizationInput sending={promiseInProgress}
                                                            callback={(auth) => {
                                                                setSelectedAuth(auth);
                                                                methods.setValue('serviceType', []);
                                                                clearRecordTypeSelect();
                                                            }}
                                                            authorizationList={authorizationList}/>
                                    }

                                    <ClientReportTypeInput disabled={isStaffingAddendum || promiseInProgress}
                                                           clientReportTypeOptions={
                                                               recordTypeOption ? recordTypeOption : clientReportTypeByAuthRecordType(selectedAuth)
                                                           }
                                                           callback={onReportTypeChanged}/>

                                    <div className="flex text-black-200 pb-2 items-center text-base">
                                        <div className="w-2/6 text-right">
                                            Include in Report Updates
                                        </div>
                                        <div className="w-3/6 ml-4 flex items-center">
                                            <ProviderPersonLookupMultipleInput
                                                fieldValues={notifyUsers}
                                                setFieldValues={setNotifyUsers}
                                                personLookupType={'ProviderUser'}
                                            />
                                        </div>
                                    </div>
                                    {
                                        isDEAuth && isDiagEvalReport(methods.getValues("reportType")) &&
                                        <>
                                            <ModalDatePicker<ClientReportSubmitInput>
                                                label="Date of Final Diagnostic Contact"
                                                disabled={promiseInProgress}
                                                field="finalDiagnosticContactDate"
                                                maxDate={new Date()}
                                            />
                                            <VinelandIncludedInput disabled={promiseInProgress} />
                                        </>
                                    }
                                    {
                                        isAddendumRequestReasonRequired(methods.getValues("reportType")) &&
                                        <AddendumRequestReasonInput sending={promiseInProgress}/>
                                    }
                                    <ClinicalRecommendationInput disabled={promiseInProgress}
                                                                 restrictedOptions={restrictedOptions}
                                                                 reportType={methods.getValues('reportType')}
                                                                 transitionRequestData={transitionRequestData}
                                                                 resetHiddenFieldsOnServiceTypeChange/>

                                    {ServiceRecommendedOptions.get(methods.getValues("serviceRecommended")) !== ServiceRecommended.No &&
                                        <ClientOfferInput disabled={promiseInProgress}
                                                          reportType={methods.getValues('reportType')}/>
                                    }

                                    {
                                        shouldShowLastDayOfService(methods.getValues("reportType")) &&
                                        <ModalDatePicker<ClientReportSubmitInput>
                                            label="Last Service Date Billed"
                                            disabled={promiseInProgress}
                                            field="lastDayOfServiceBilled"
                                        />
                                    }
                                    {

                                        shouldShowNewAuthStartDate(methods.getValues("reportType")) &&
                                        <ModalDatePicker<ClientReportSubmitInput>
                                            label="New Authorization Start Date"
                                            disabled={promiseInProgress}
                                            field="newAuthorizationStartDate"
                                            maxDate={defaultAuth?.endDate ? parse(defaultAuth.endDate, "yyyy-MM-dd", new Date()) : undefined}
                                        />
                                    }

                                    {!isShortAddendum && (<>
                                        <CommentsInput sending={promiseInProgress}/>
                                        <ScannedFileInput uploadedFiles={uploadedFiles}
                                                          isFileValid$={isScannedFileValid$}
                                                          shouldValidate$={shouldValidate$}
                                                          mustProvideBothPdfAndDoc={isTherapyAuth}
                                                          mustIncludeVineland={mustIncludeVineland}
                                                          onlySupportPdf={!isTherapyAuth}
                                                          setUploadedFiles={setUploadedFiles}
                                                          accessToken={accessToken}/>
                                    </>)}

                                    {isShortAddendum && (<>
                                        <ShortAddendumInput disabled={promiseInProgress}/>
                                    </>)}

                                    {
                                        (inReviewReportTypes.length > 0 || authorizedReportTypes.length > 0) &&
                                        <div className="flex text-gray-600 items-center text-xs justify-end column">
                                            <ul className="text-gray-600 text-xs">
                                                {
                                                    inReviewReportTypes.length > 0 &&
                                                    <li>
                                                        <span className="font-bold">In Review Report Types:</span> {inReviewReportTypes?.map((report) => ClientReportReportTypeMap.get(report.reportType)).join(', ')}
                                                    </li>
                                                }
                                                {
                                                    authorizedReportTypes.length > 0 &&
                                                    <li>
                                                        <span className="font-bold">Authorized Report Types:</span> {authorizedReportTypes?.map((report) => ClientReportReportTypeMap.get(report.reportType)).join(', ')}
                                                    </li>
                                                }
                                            </ul>
                                        </div>
                                    }
                                    </>)}
                                </div>
                            </div>

                            <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                                <div className="flex w-1/4">
                                    <FatOutlineButton color={'red'}
                                                      text={'Cancel'}
                                                      onClick={cancel}/>
                                    <div style={{width: 10}}/>
                                    <FatOutlineButton disabled={promiseInProgress} type="submit" text={"Submit"}
                                                      color={"green"}/>
                                </div>
                            </div>
                        </React.Fragment>
                    }
                    {
                        (isFetchingLatestHoursData || promiseInProgress) &&
                        <div className="p-24">
                            <LoadingSpinner text={isFetchingLatestHoursData ? 'Loading' : 'Submitting'}/>
                        </div>
                    }
                    <ClientReportDialogManager 
                        auth={selectedAuth || defaultAuth}
                        show$={showConfirmationDialog$}
                        selectedReportType={methods.watch('reportType')}
                        inReviewReportTypes={inReviewReportTypes}
                        authorizedReportTypes={authorizedReportTypes}
                        cancelCallback={() => {
                            showConfirmationDialog$.next(false);
                            cancel();
                        }}
                        submitCallback={(params: ClientReportDialogSubmitParams) => {
                            submitWithFormData({
                                data: methods.getValues(),
                                isReportFileValid: true,
                                shouldForceCreateNewOperationCase: params.shouldForceCreateNewOperationCase,
                                reviseReportType: params.reviseReportType,
                                pendingAuthToUpdate: params.pendingAuthToUpdate,
                                shouldUpdateActiveAuth: params.shouldUpdateActiveAuth
                            });
                            showConfirmationDialog$.next(false);
                        }}
                        />
                </form>
            </FormProvider>
        </ReportModal>
    );
}
